/**
 * Curve2D and Curve3D control points from a resource body, as Godot loads them: `_data`
 * through `_set_data`, then `point_count` through `set_point_count`, the order Godot's
 * writer emits them in. The two curves differ only in their {@link BezierCurveReader}.
 * No THREE.
 */
import { slotInt } from '../../../godot/variant/intSlots.js';
import { findSubResourceOfType, parseResourceReference } from '../../SubResourceResolver';
import { readBezierData } from './bezierData';
import { resizeBezierPoints } from './pointCount';
/**
 * The control points of a Curve2D or Curve3D body. A `_data` that `_set_data` refuses,
 * or that does not parse, loads no points.
 */
function decodeBezierCurve(data, reader) {
    const points = readControlPoints(data._data, reader);
    const count = data.point_count === undefined ? null : slotInt(data.point_count);
    return count === null ? points : resizeBezierPoints(points, count, reader.origin);
}
/**
 * The control points of the curve a `SubResource("id")` value names: the first
 * resource of the reader's class under that id, the one the path linters check.
 * No points for any other value, or an id that names no such resource.
 */
export function resolveBezierCurve(ref, internalResources, reader) {
    const parsed = ref === undefined ? null : parseResourceReference(ref);
    if (parsed?.type !== 'SubResource')
        return [];
    const resource = findSubResourceOfType(internalResources, parsed.id, reader.format.className);
    return resource ? decodeBezierCurve(resource.data, reader) : [];
}
function readControlPoints(value, reader) {
    if (value === undefined)
        return [];
    const read = readBezierData(value, reader.format);
    if (read.refusal !== null)
        return [];
    let flat;
    try {
        flat = reader.parse(read.loaded.points);
    }
    catch {
        return [];
    }
    const points = [];
    for (let i = 0; i < read.loaded.controlPoints; i++) {
        points.push(reader.pointAt(flat, i * reader.format.floatsPerPoint));
    }
    return points;
}
//# sourceMappingURL=bezierCurve.js.map